/* eslint-disable react/prop-types */
import { graphql } from 'gatsby';
import { GatsbyImage, getImage } from 'gatsby-plugin-image';
import React from 'react';

import SocialShare from 'components/pages/blog-post/social-share/social-share';
import Button from 'components/shared/button/button';
import CollapsibleContent from 'components/shared/collapsible-content';
import Content from 'components/shared/content/content';
import Layout from 'components/shared/layout';
import RelatedPostCardList from 'components/shared/related-post-card-list';
import RequestDemo from 'components/shared/request-demo/request-demo';
import Spacing from 'components/shared/spacing';
import Subscribe from 'components/shared/subscribe';
import VideoEmbed from 'components/shared/video-embed';
import dateFormatter from 'utils/date-formatter';

const WebinarPost = ({
  data: {
    wpEvent: {
      title,
      slug,
      pageEvent: {
        isUpcoming,
        shortDescription,
        description,
        transcript,
        dateStart,
        cover,
        filledButtonTitle,
        filledButtonLink,
        borderButtonTitle,
        borderButtonLink,
        videoType,
        videoId,
      },
      seo,
    },
    requestDemo: { sharedBlockRequestDemo: requestDemoData },
    relatedWebinars,
  },
  pageContext,
  location,
}) => {
  const requestDemo = {
    title: requestDemoData.title,
    description: requestDemoData.description,
    inputPlaceholder: requestDemoData.inputPlaceholder,
    buttonText: requestDemoData.buttonText,
    buttonUrl: requestDemoData.buttonUrl,
    linkText: requestDemoData.linkText,
    linkUrl: requestDemoData.linkUrl,
    caseStudyText: requestDemoData.caseStudy.text,
    caseStudyAuthorImage: {
      url: requestDemoData.caseStudy.authorImage.localFile.publicURL,
    },
    caseStudyAuthorName: requestDemoData.caseStudy.authorName,
    caseStudyLinkText: requestDemoData.caseStudy.link.title,
    caseStudyLinkUrl: requestDemoData.caseStudy.link.url,
  };

  const hasFilledButton = !!filledButtonTitle && !!filledButtonLink;
  const hasBorderButton = !!borderButtonTitle && !!borderButtonLink;
  const hasButtons = !!isUpcoming && (hasFilledButton || hasBorderButton);

  return (
    <Layout
      seo={{
        ...seo,
        metaDesc: seo.metaDesc || title,
        opengraphUrl: `/webinars/${slug}/`,
      }}
      pageContext={pageContext}
      headerTheme="white"
    >
      <div className="container-xs">
        <article className="safe-paddings pt-16 lg:pt-5 md:pt-8">
          <h1 className="text-5xl font-bold leading-denser md:text-[28px]">{title}</h1>
          <p
            className="mt-3 text-xl font-normal leading-normal text-gray-9"
            dangerouslySetInnerHTML={{ __html: shortDescription }}
          />

          {hasButtons && (
            <div className="flex flex-row items-center justify-start space-x-4 sm:flex-col sm:space-x-0">
              {hasFilledButton && (
                <Button
                  className="mt-5 sm:w-full"
                  to={filledButtonLink}
                  size="md"
                  theme="secondary-2"
                >
                  {filledButtonTitle}
                </Button>
              )}
              {hasBorderButton && (
                <Button
                  className="mt-5 md:mt-4 sm:w-full"
                  to={borderButtonLink}
                  size="md"
                  theme="secondary-2-border"
                >
                  {borderButtonTitle}
                </Button>
              )}
            </div>
          )}

          {!!videoId && !!videoType ? (
            <VideoEmbed
              className="mt-8 lg:mt-7 md:mt-5"
              embedId={videoId}
              embedType={videoType}
              videoHeight={450}
              videoWidth={800}
            />
          ) : (
            <div className="mt-8 w-full lg:mt-7 md:mt-5">
              {cover && (
                <GatsbyImage
                  className="w-full rounded-lg bg-gray-1"
                  imgClassName="w-full"
                  image={getImage(cover?.localFile)}
                  alt={cover?.altText || ''}
                />
              )}
            </div>
          )}

          <div className="mt-6 flex flex-row items-center justify-between">
            <span className="text-sm leading-tight text-gray-8">
              {dateFormatter(dateStart, {
                weekday: 'long',
                year: 'numeric',
                hour: 'numeric',
                minute: 'numeric',
              })}
            </span>
            <SocialShare title={title} url={location.href} />
          </div>

          {description && <Content className="mt-10 md:mt-8" content={description} asHTML />}
        </article>

        <div className="mt-14 border-t border-gray-4 md:mt-8">
          {transcript && (
            <CollapsibleContent
              title="Transcript"
              description={transcript}
              className="pt-10 lg:pt-8 md:pt-5"
            />
          )}
          <Subscribe
            className="mt-9 lg:mt-8 md:mt-7"
            title="Subscribe to newsletters"
            inputPlaceholder="Your email address..."
            buttonText="Subscribe"
            orientation="vertical"
          />
        </div>
      </div>

      {relatedWebinars.nodes && (
        <RelatedPostCardList
          items={relatedWebinars.nodes.map((item) => ({
            url: `/webinars/${item.slug}/`,
            cover: item.pageEvent.cover,
            publishedAt: dateFormatter(item.pageEvent.dateStart, { year: 'numeric' }),
            title: item.title,
            description: item.pageEvent.shortDescription,
          }))}
        />
      )}

      <Spacing size="md" />
      <RequestDemo {...requestDemo} />
    </Layout>
  );
};

export const query = graphql`
  query ($id: String!, $locale: String!) {
    wpEvent(id: { eq: $id }) {
      title
      slug
      pageEvent {
        isUpcoming
        shortDescription
        description
        transcript
        videoType
        videoId
        dateStart
        filledButtonTitle
        filledButtonLink
        borderButtonTitle
        borderButtonLink
        cover {
          localFile {
            childImageSharp {
              gatsbyImageData(width: 800)
            }
          }
          altText
        }
      }
      ...wpEventSeo
    }

    relatedWebinars: allWpEvent(
      filter: {
        id: { ne: $id }
        pageEvent: { isUpcoming: { ne: true }, category: { eq: "webinar" } }
      }
      limit: 3
      sort: { pageEvent: { dateStart: DESC } }
    ) {
      nodes {
        pageEvent {
          isUpcoming
          shortDescription
          dateStart
          videoType
          videoId
          cover {
            localFile {
              childImageSharp {
                gatsbyImageData(width: 384, height: 216)
              }
            }
            altText
          }
        }
        link
        title
        slug
      }
    }

    requestDemo: wpSharedBlock(
      slug: { eq: "request-demo" }
      language: { locale: { eq: $locale } }
    ) {
      sharedBlockRequestDemo {
        title
        description
        inputPlaceholder
        buttonText
        buttonUrl
        linkText
        linkUrl
        caseStudy {
          text
          authorImage {
            localFile {
              publicURL
            }
          }
          authorName
          link {
            url
            title
          }
        }
      }
    }
  }
`;

export default WebinarPost;
